import React, { useEffect, useState } from "react";
import WebButton from "../../../../component/library/webButton";
import { userApiService } from "../../../../service/admin/userApiService";
import { useLoader } from "../../../../base/context/LoaderProvider";
import {
  ToastTypes,
  useMessage,
} from "../../../../base/context/MessageProvider";
import { useNavigate, useParams } from "react-router-dom";

const EditUser = () => {
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    role: "",
    email: "",
    companyName: "",
    gst: "",
    phone: "",
    country: "",
    state: "",
    address: "",
    zipcode: "",
    language: "",
    pincode: "",
    address1: "",
    address2: "",
    city: "",
    bankAccountNumber: "",
    bankIfscCode: "",
    bankAddress: "",
    bankAccountHolderName: "",
    BusinessName: "",
    BusinessDescription: "",
    tan: "",
    GstinNumber: "",
    registerBusinessAddress: "",
    referredBy: "",
    settings: {
      amazonSpPercent: 0,
      amazonMrpPercent: 0,
      shopifySpPercent: 0,
      shopifyMrpPercent: 0,
      inventorySync: {
        amazon: false,
        shopify: false,
      },
      priceSync: {
        amazon: false,
        shopify: false,
      },
      brandName: "",
      brandImage: "",
      companyName: "",
    },
  });

  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();
  const params = useParams();

  useEffect(() => {
    getUserById();
  }, []);

  const getUserById = () => {
    loader.showLoading(true);
    userApiService
      .getUserByID(params.id)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          const data = res.data || {};
          // Safely merge settings so defaults remain if API doesn't send all keys
          setUserData((prev) => ({
            ...prev,
            ...data,
            settings: {
              ...prev.settings,
              ...(data.settings || {}),
              inventorySync: {
                ...prev.settings.inventorySync,
                ...(data.settings?.inventorySync || {}),
              },
              priceSync: {
                ...prev.settings.priceSync,
                ...(data.settings?.priceSync || {}),
              },
            },
          }));
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

  const handleOnChange = (e) => {
    const { name, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    // Handle nested "settings.*" paths like "settings.amazonSpPercent" or "settings.inventorySync.amazon"
    if (name.startsWith("settings.")) {
      const path = name.split("."); // ["settings", "amazonSpPercent"] or ["settings","inventorySync","amazon"]

      setUserData((prev) => {
        const newSettings = { ...prev.settings };

        if (path.length === 2) {
          // e.g. settings.amazonSpPercent
          newSettings[path[1]] = inputValue;
        } else if (path.length === 3) {
          // e.g. settings.inventorySync.amazon
          const parentKey = path[1]; // inventorySync or priceSync
          const childKey = path[2]; // amazon / shopify
          newSettings[parentKey] = {
            ...prev.settings[parentKey],
            [childKey]: inputValue,
          };
        }

        return {
          ...prev,
          settings: newSettings,
        };
      });
    } else {
      // Top-level fields
      setUserData((prevState) => ({
        ...prevState,
        [name]: inputValue,
      }));
    }
  };

  const handleEdit = (e) => {
    e.preventDefault();
    loader.showLoading(true);
    userApiService
      .updateUser(params.id, userData)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast("User updated successfully", ToastTypes.SUCCESS);
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

  const inputStyle =
    "block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const labelStyle = "block text-sm font-medium text-slate-700 mb-1";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="w-full mx-auto px-0 py-2 sm:py-3">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-blue-900 tracking-tight">Edit User</h1>
            <p className="mt-0.5 text-xs sm:text-sm text-slate-500">Update user information, business details and platform settings.</p>
          </div>
          <WebButton
            type="button"
            text="Back to Users"
            className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs sm:text-sm font-medium text-slate-700 bg-white hover:bg-slate-100 transition"
            onClick={() => navigate("/admin/user-management")}
          />
        </div>

        {/* Card */}
        <div className="bg-white/90 shadow-lg rounded-xl p-1 sm:p-2 lg:p-3 border border-slate-100 w-full">
          <form onSubmit={handleEdit} className="space-y-2 w-full">
            {/* Grid: left + right column on large screens */}
            <div className="grid gap-2 lg:gap-3 lg:grid-cols-2 w-full">
              {/* LEFT COLUMN */}
              <div className="space-y-2 w-full">
                {/* Basic Info */}
                <section className="rounded-lg border border-slate-100 bg-slate-50/60 p-2 pb-1 w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-1.5 h-4 bg-blue-400 rounded-full mr-1" />
                    <h2 className="text-base font-semibold text-blue-800 tracking-tight">Basic Information</h2>
                  </div>
                  <div className="grid gap-1 sm:grid-cols-2 w-full">
                    <div>
                      <label className={labelStyle}>First Name</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.firstName}
                        name="firstName"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Last Name</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.lastName}
                        name="lastName"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Gender</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.gender}
                        name="gender"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Role</label>
                      <select
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.role}
                        name="role"
                      >
                        <option value="">--Select Role--</option>
                        <option value="seller">Seller</option>
                        <option value="admin">Admin</option>
                        <option value="merchant">Merchant</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelStyle}>Email</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.email}
                        name="email"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Company Name</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.companyName}
                        name="companyName"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Referred By</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.referredBy}
                        name="referredBy"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>GSTIN</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.gst}
                        name="gst"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Phone</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.phone}
                        name="phone"
                      />
                    </div>
                  </div>
                </section>

                {/* Address */}
                <section className="rounded-lg border border-slate-100 bg-slate-50/60 p-2 pb-1 w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-1.5 h-4 bg-blue-400 rounded-full mr-1" />
                    <h2 className="text-base font-semibold text-blue-800 tracking-tight">Address</h2>
                  </div>
                  <div className="grid gap-1 sm:grid-cols-2 w-full">
                    <div className="sm:col-span-2">
                      <label className={labelStyle}>Address 1</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.address1}
                        name="address1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelStyle}>Address 2</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.address2}
                        name="address2"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>City</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.city}
                        name="city"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>State</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.state}
                        name="state"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Pincode</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.pincode}
                        name="pincode"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Zipcode</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.zipcode}
                        name="zipcode"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelStyle}>Country</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.country}
                        name="country"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelStyle}>Language</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.language}
                        name="language"
                      />
                    </div>
                  </div>
                </section>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-2 w-full">
                {/* Bank Details */}
                <section className="rounded-lg border border-slate-100 bg-slate-50/60 p-2 pb-1 w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-1.5 h-4 bg-blue-400 rounded-full mr-1" />
                    <h2 className="text-base font-semibold text-blue-800 tracking-tight">Bank Details</h2>
                  </div>
                  <div className="grid gap-1 grid-cols-2 w-full">
                    <div>
                      <label className={labelStyle}>Bank Account Number</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.bankAccountNumber}
                        name="bankAccountNumber"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Bank Account Holder Name</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.bankAccountHolderName}
                        name="bankAccountHolderName"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Bank IFSC Code</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.bankIfscCode}
                        name="bankIfscCode"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Bank Address</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.bankAddress}
                        name="bankAddress"
                      />
                    </div>
                  </div>
                </section>

                {/* Business Info */}
                <section className="rounded-lg border border-slate-100 bg-slate-50/60 p-2 pb-1 w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-1.5 h-4 bg-blue-400 rounded-full mr-1" />
                    <h2 className="text-base font-semibold text-blue-800 tracking-tight">Business Information</h2>
                  </div>
                  <div className="grid gap-1 grid-cols-2 w-full">
                    <div className="col-span-2 flex gap-2">
                      <div className="w-1/2">
                        <label className={labelStyle}>Business Name</label>
                        <input
                          className={inputStyle}
                          onChange={handleOnChange}
                          value={userData.BusinessName}
                          name="BusinessName"
                        />
                      </div>
                      <div className="w-1/2">
                        <label className={labelStyle}>Business Description</label>
                        <input
                          className={inputStyle}
                          onChange={handleOnChange}
                          value={userData.BusinessDescription}
                          name="BusinessDescription"
                        />
                      </div>
                    </div>
                    <div>
                      <label className={labelStyle}>TAN</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.tan}
                        name="tan"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>GSTIN Number</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.GstinNumber}
                        name="GstinNumber"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className={labelStyle}>Registered Business Address</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.registerBusinessAddress}
                        name="registerBusinessAddress"
                      />
                    </div>
                  </div>
                </section>

                {/* Platform Settings */}
                <section className="rounded-lg border border-slate-100 bg-slate-50/60 p-2 pb-1 w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-1.5 h-4 bg-blue-400 rounded-full mr-1" />
                    <h2 className="text-base font-semibold text-blue-800 tracking-tight">Platform Settings</h2>
                  </div>
                  <div className="grid gap-1 sm:grid-cols-2 w-full">
                    <div>
                      <label className={labelStyle}>Amazon SP %</label>
                      <input
                        className={inputStyle}
                        type="number"
                        onChange={handleOnChange}
                        value={userData.settings.amazonSpPercent}
                        name="settings.amazonSpPercent"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Amazon MRP %</label>
                      <input
                        className={inputStyle}
                        type="number"
                        onChange={handleOnChange}
                        value={userData.settings.amazonMrpPercent}
                        name="settings.amazonMrpPercent"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Shopify SP %</label>
                      <input
                        className={inputStyle}
                        type="number"
                        onChange={handleOnChange}
                        value={userData.settings.shopifySpPercent}
                        name="settings.shopifySpPercent"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Shopify MRP %</label>
                      <input
                        className={inputStyle}
                        type="number"
                        onChange={handleOnChange}
                        value={userData.settings.shopifyMrpPercent}
                        name="settings.shopifyMrpPercent"
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        onChange={handleOnChange}
                        checked={userData.settings.inventorySync.amazon}
                        name="settings.inventorySync.amazon"
                      />
                      Amazon Inventory Sync
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        onChange={handleOnChange}
                        checked={userData.settings.inventorySync.shopify}
                        name="settings.inventorySync.shopify"
                      />
                      Shopify Inventory Sync
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        onChange={handleOnChange}
                        checked={userData.settings.priceSync.amazon}
                        name="settings.priceSync.amazon"
                      />
                      Amazon Price Sync
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        onChange={handleOnChange}
                        checked={userData.settings.priceSync.shopify}
                        name="settings.priceSync.shopify"
                      />
                      Shopify Price Sync
                    </label>
                  </div>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelStyle}>Brand Name</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.settings.brandName}
                        name="settings.brandName"
                      />
                    </div>
                    <div>
                      <label className={labelStyle}>Brand Image</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.settings.brandImage}
                        name="settings.brandImage"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelStyle}>Company Name</label>
                      <input
                        className={inputStyle}
                        onChange={handleOnChange}
                        value={userData.settings.companyName}
                        name="settings.companyName"
                      />
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {/* Actions - sticky on mobile */}
            <div className="w-full flex justify-end gap-2 mt-2">
              <WebButton
                type="button"
                text="Cancel"
                className="px-4 py-2 rounded-lg border border-slate-300 text-xs sm:text-sm font-medium text-slate-700 bg-white hover:bg-slate-100 transition"
                onClick={() => navigate("/admin/user-management")}
              />
              <WebButton
                type="submit"
                text="Update"
                className="px-4 py-2 rounded-lg bg-blue-600 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
